/**
 * The hash-route parser, and the one place that knows which pages are
 * admin-only.
 *
 * parseRoute lived in App.tsx until a production login loop made it clear that
 * the post-login redirect needs the SAME parser the router uses. A second,
 * lookalike parser is exactly how that class of bug comes back, so it lives
 * here and App re-exports it.
 */
export function parseRoute(hash: string): { page: string; param?: string; param2?: string } {
  // Strip a query string (e.g. #/login?redirect=…) before splitting on "/".
  const clean = hash.replace(/^#\/?/, "").split("?")[0];
  const parts = clean.split("/").filter(Boolean);
  if (parts.length === 0) return { page: "home" };
  if (parts[0] === "admin" && parts[1] === "chains" && parts[2] === "duplicates") return { page: "admin-duplicate-chains" };
  if (parts[0] === "admin" && parts[1] === "signups") return { page: "signups" };
  if (parts[0] === "admin" && parts[1] === "intake") return { page: "admin-intake" };
  if (parts[0] === "admin" && parts[1] === "distributors" && parts[2] === "bridge") return { page: "distributor-bridge" };
  if (parts[0] === "admin" && parts[1] === "mappings" && parts[2] === "ad-spend") return { page: "admin-mappings-adspend" };
  if (parts[0] === "login") return { page: "login" };
  if (parts[0] === "forgot-password") return { page: "forgot-password" };
  if (parts[0] === "reset-password") return { page: "reset-password" };
  if (parts[0] === "brands" && parts[1] === "signup") return { page: "brands-signup" };
  if (parts[0] === "brand" && parts[1]) {
    const slug = decodeURIComponent(parts[1]);
    if (parts[2] === "onboard") return { page: "brand-onboard", param: slug };
    if (parts[2] === "accounts" && parts[3]) return { page: "brand-account-detail", param: slug, param2: decodeURIComponent(parts[3]) };
    if (parts[2] === "accounts") return { page: "brand-accounts", param: slug };
    if (parts[2] === "my-chains") return { page: "brand-my-chains", param: slug };
    if (parts[2] === "chains" && parts[3]) return { page: "brand-chain-detail", param: slug, param2: decodeURIComponent(parts[3]) };
    if (parts[2] === "chains") return { page: "brand-chains", param: slug };
    if (parts[2] === "activity") return { page: "brand-activity", param: slug };
    if (parts[2] === "changes") return { page: "brand-changes", param: slug };
    if (parts[2] === "mappings" && parts[3] === "ad-spend") return { page: "brand-mappings-adspend", param: slug };
    if (parts[2] === "upload") {
      if (parts[3] === "cm") return { page: "brand-upload-cm", param: slug };
      if (parts[3] === "wtb") return { page: "brand-upload-wtb", param: slug };
      if (parts[3] === "adspend") return { page: "brand-upload-adspend", param: slug };
    }
    return { page: "brand-review", param: slug };
  }
  if (parts[0] === "upload") {
    if (parts[1] === "wtb") return { page: "upload-wtb" };
    if (parts[1] === "cm") return { page: "upload-cm" };
    return { page: "upload" };
  }
  if (parts[0] === "review") {
    if (parts[1] === "enrich" && parts[2]) return { page: "enrich", param: decodeURIComponent(parts[2]) };
    return { page: "review" };
  }
  if (parts[0] === "activity") return { page: "activity" };
  if (parts[0] === "chains") {
    if (parts[1]) return { page: "chain-detail", param: decodeURIComponent(parts[1]) };
    return { page: "chains" };
  }
  if (parts[0] === "my-chains") return { page: "my-chains" };
  if (parts[0] === "changes") return { page: "changes" };
  if (parts[0] === "tenants") {
    if (parts[1]) return { page: "tenant-detail", param: decodeURIComponent(parts[1]) };
    return { page: "tenants" };
  }
  if (parts[0] === "signups") return { page: "signups" };
  if (parts[0] === "gbp-ambiguity") return { page: "gbp-ambiguity" };
  if (parts[0] === "done") return { page: "done" };
  return { page: "home" };
}

/**
 * Every page wrapped in <RequireAdmin> in App.tsx.
 *
 * This list is not decoration: it is what stops the post-login redirect from
 * bouncing a non-admin straight back into a wall. `post-login.test.ts` reads
 * App.tsx and fails if a RequireAdmin route is missing here, so the two cannot
 * drift apart.
 */
export const ADMIN_ONLY_PAGES: ReadonlySet<string> = new Set([
  "upload",
  "upload-cm",
  "upload-wtb",
  "review",
  "enrich",
  "admin-duplicate-chains",
  "tenants",
  "tenant-detail",
  "signups",
  "distributor-bridge",
  "admin-mappings-adspend",
  "gbp-ambiguity",
  "admin-intake",
]);

export function isAdminOnlyHash(hash: string): boolean {
  return ADMIN_ONLY_PAGES.has(parseRoute(hash).page);
}
